import { useMemo } from "react";
import { DocumentCard } from "./document-card";
import { FolderCard } from "./folder-card";
import type { DocumentFile } from "@/types/documents";
import type { Container } from "@/types/container";
import type { CustomFolder } from "@/types/documents";
import type { ViewMode } from "@/types/documents";
import { ChevronDown, ChevronUp } from "@untitledui/icons";

export type FolderSortField = "name" | "documentCount" | "updatedAt";
export type DocumentSortField = "name" | "uploadedAt" | "updatedAt";
export type SortDirection = "asc" | "desc";

// Union type for combined folder items
type FolderItem =
    | { type: "custom_folder"; folder: CustomFolder }
    | { type: "container"; container: Container };

interface SortableHeaderProps<T extends string> {
    label: string;
    field: T;
    currentSortField?: T;
    currentSortDirection?: SortDirection;
    onSort?: (field: T) => void;
    className?: string;
}

function SortableHeader<T extends string>({ label, field, currentSortField, currentSortDirection, onSort, className }: SortableHeaderProps<T>) {
    const isActive = currentSortField === field;
    const canSort = !!onSort;

    return (
        <button
            type="button"
            className={`flex items-center gap-1 ${canSort ? "cursor-pointer hover:text-primary" : ""} ${className || ""}`}
            onClick={() => onSort?.(field)}
            disabled={!canSort}
        >
            {label}
            {canSort && isActive && (
                currentSortDirection === "asc"
                    ? <ChevronUp className="size-3" />
                    : <ChevronDown className="size-3" />
            )}
        </button>
    );
}

/**
 * Header row component for folders in list view
 */
interface FolderListViewHeaderProps {
    sortField?: FolderSortField;
    sortDirection?: SortDirection;
    onSort?: (field: FolderSortField) => void;
}

const FolderListViewHeader = ({ sortField, sortDirection, onSort }: FolderListViewHeaderProps) => (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-secondary text-xs font-medium text-tertiary uppercase tracking-wide">
        {/* Folder Icon + Name */}
        <div className="w-6 shrink-0" /> {/* Placeholder for icon */}
        <SortableHeader
            label="Name"
            field="name"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="flex-1 min-w-0"
        />

        {/* Document Count */}
        <SortableHeader
            label="Documents"
            field="documentCount"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="w-[100px] shrink-0"
        />

        {/* Last Modified */}
        <SortableHeader
            label="Modified"
            field="updatedAt"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="w-[130px] shrink-0"
        />
    </div>
);

/**
 * Header row component for documents in list view
 */
interface ListViewHeaderProps {
    sortField?: DocumentSortField;
    sortDirection?: SortDirection;
    onSort?: (field: DocumentSortField) => void;
}

const ListViewHeader = ({ sortField, sortDirection, onSort }: ListViewHeaderProps) => (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-secondary text-xs font-medium text-tertiary uppercase tracking-wide">
        {/* File Icon + Name */}
        <div className="w-10 shrink-0" /> {/* Placeholder for icon */}
        <SortableHeader<DocumentSortField>
            label="Name"
            field="name"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="flex-1 min-w-0 min-w-[200px]"
        />

        {/* Document Type (Category) */}
        <div className="w-[120px] shrink-0">Category</div>

        {/* Extraction Status */}
        <div className="w-[100px] shrink-0">Extraction</div>

        {/* Validation Status */}
        <div className="w-[100px] shrink-0">Validation</div>

        {/* Version */}
        <div className="w-[90px] shrink-0">Version</div>

        {/* Upload Method */}
        <div className="w-[80px] shrink-0">Source</div>

        {/* Upload Time */}
        <SortableHeader<DocumentSortField>
            label="Uploaded"
            field="uploadedAt"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="w-[120px] shrink-0"
        />

        {/* Last Modified */}
        <SortableHeader<DocumentSortField>
            label="Modified"
            field="updatedAt"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={onSort}
            className="w-[120px] shrink-0"
        />

        {/* Actions */}
        <div className="w-[40px] shrink-0"></div>
    </div>
);

interface DocumentListProps {
    documents?: DocumentFile[];
    containers?: Container[];
    customFolders?: CustomFolder[];
    viewMode?: ViewMode;
    selectedItems?: string[];
    onDocumentClick?: (document: DocumentFile) => void;
    onDocumentContextMenu?: (document: DocumentFile, e: React.MouseEvent) => void;
    onDocumentCheckboxChange?: (document: DocumentFile, checked: boolean) => void;
    /** Callback quando si seleziona una versione dal dropdown versioni */
    onVersionSelect?: (documentId: string, version: number) => void;
    onContainerClick?: (container: Container) => void;
    onContainerContextMenu?: (container: Container, e: React.MouseEvent) => void;
    onContainerCheckboxChange?: (container: Container, checked: boolean) => void;
    onCustomFolderClick?: (folder: CustomFolder) => void;
    onCustomFolderContextMenu?: (folder: CustomFolder, e: React.MouseEvent) => void;
    onCustomFolderCheckboxChange?: (folder: CustomFolder, checked: boolean) => void;
    className?: string;
    containerDocumentCounts?: Map<string, number>; // Map container ID -> document count
    customFolderDocumentCounts?: Map<string, number>; // Map custom folder ID -> document count
    containerUpdatedAt?: Map<string, Date | string>; // Map container ID -> updated_at
    customFolderUpdatedAt?: Map<string, Date | string>; // Map custom folder ID -> updated_at
    containerCreatedAt?: Map<string, Date | string>; // Map container ID -> created_at
    customFolderCreatedAt?: Map<string, Date | string>; // Map custom folder ID -> created_at
    // Sorting props for folders
    folderSortField?: FolderSortField;
    folderSortDirection?: SortDirection;
    onFolderSort?: (field: FolderSortField) => void;
    // Sorting props for documents
    documentSortField?: DocumentSortField;
    documentSortDirection?: SortDirection;
    onDocumentSort?: (field: DocumentSortField) => void;
}

export const DocumentList = ({
    documents = [],
    containers = [],
    customFolders = [],
    viewMode = "card",
    selectedItems = [],
    onDocumentClick,
    onDocumentContextMenu,
    onDocumentCheckboxChange,
    onVersionSelect,
    onContainerClick,
    onContainerContextMenu,
    onContainerCheckboxChange,
    onCustomFolderClick,
    onCustomFolderContextMenu,
    onCustomFolderCheckboxChange,
    className,
    containerDocumentCounts,
    customFolderDocumentCounts,
    containerUpdatedAt,
    customFolderUpdatedAt,
    containerCreatedAt,
    customFolderCreatedAt,
    folderSortField,
    folderSortDirection,
    onFolderSort,
    documentSortField,
    documentSortDirection,
    onDocumentSort,
}: DocumentListProps) => {
    const containerClass = viewMode === "card"
        ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
        : "space-y-1";

    // Determine content scenario
    const hasFolders = customFolders.length > 0 || containers.length > 0;
    const hasDocuments = documents.length > 0;

    // Three scenarios for list view:
    // 1. Folders only: Use folder-specific header (Name, Documents, Modified)
    // 2. Documents only: Use document header (Name, Category, Status, Source, Uploaded, Modified)
    // 3. Mixed (folders + documents): Use document header, folders use mixed layout
    const isMixedList = hasFolders && hasDocuments;

    // Show appropriate header based on scenario
    // Folder header only when folders-only (no documents)
    const showFolderHeader = viewMode === "list" && hasFolders && !hasDocuments;
    // Document header when there are documents (mixed or documents-only)
    const showDocumentHeader = viewMode === "list" && hasDocuments;

    // Combine and sort all folders together
    const sortedFolders = useMemo(() => {
        // Combine custom folders and containers into a single array
        const allFolders: FolderItem[] = [
            ...customFolders.map((folder) => ({ type: "custom_folder" as const, folder })),
            ...containers.map((container) => ({ type: "container" as const, container })),
        ];

        // Helper functions to get values for sorting
        const getName = (item: FolderItem): string => {
            if (item.type === "custom_folder") {
                return item.folder.name || "";
            }
            return item.container.containerNumber || "";
        };

        const getDocumentCount = (item: FolderItem): number => {
            if (item.type === "custom_folder") {
                return customFolderDocumentCounts?.get(item.folder.id) ?? 0;
            }
            return containerDocumentCounts?.get(item.container.id) ?? 0;
        };

        const getUpdatedAt = (item: FolderItem): number => {
            let dateValue: Date | string | undefined;
            if (item.type === "custom_folder") {
                dateValue = customFolderUpdatedAt?.get(item.folder.id) ?? item.folder.updatedAt;
            } else {
                dateValue = containerUpdatedAt?.get(item.container.id) ?? item.container.updatedAt;
            }
            if (!dateValue) return 0;
            const timestamp = new Date(dateValue).getTime();
            return Number.isNaN(timestamp) ? 0 : timestamp;
        };

        // Sort the combined array
        const sortField = folderSortField || "updatedAt";
        const sortDirection = folderSortDirection || "desc";

        return [...allFolders].sort((a, b) => {
            let comparison = 0;

            switch (sortField) {
                case "name":
                    comparison = getName(a).localeCompare(getName(b));
                    break;
                case "documentCount":
                    comparison = getDocumentCount(a) - getDocumentCount(b);
                    break;
                case "updatedAt":
                    comparison = getUpdatedAt(a) - getUpdatedAt(b);
                    break;
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [
        customFolders,
        containers,
        folderSortField,
        folderSortDirection,
        containerDocumentCounts,
        customFolderDocumentCounts,
        containerUpdatedAt,
        customFolderUpdatedAt,
    ]);

    // Sort documents
    const sortedDocuments = useMemo(() => {
        if (!documentSortField || !documentSortDirection) {
            return documents;
        }

        return [...documents].sort((a, b) => {
            let comparison = 0;

            switch (documentSortField) {
                case "name":
                    comparison = (a.name || "").localeCompare(b.name || "");
                    break;
                case "uploadedAt": {
                    const aDate = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
                    const bDate = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
                    comparison = aDate - bDate;
                    break;
                }
                case "updatedAt": {
                    const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    comparison = aDate - bDate;
                    break;
                }
            }

            return documentSortDirection === "asc" ? comparison : -comparison;
        });
    }, [documents, documentSortField, documentSortDirection]);

    return (
        <div className={className}>
            {/* Folder Header - shown only when folders-only (no documents) in list view */}
            {showFolderHeader && <FolderListViewHeader sortField={folderSortField} sortDirection={folderSortDirection} onSort={onFolderSort} />}

            {/* Document Header - shown when mixed or documents-only in list view */}
            {showDocumentHeader && <ListViewHeader sortField={documentSortField} sortDirection={documentSortDirection} onSort={onDocumentSort} />}

            <div className={containerClass}>
            {/* Combined sorted folders (custom folders and containers interleaved by sort order) */}
            {sortedFolders.map((item) => {
                if (item.type === "custom_folder") {
                    const folder = item.folder;
                    return (
                        <FolderCard
                            key={`folder-${folder.id}`}
                            type="custom_folder"
                            customFolder={folder}
                            viewMode={viewMode}
                            useMixedLayout={isMixedList}
                            isSelected={selectedItems.includes(`folder-${folder.id}`)}
                            onClick={() => onCustomFolderClick?.(folder)}
                            onContextMenu={(e) => onCustomFolderContextMenu?.(folder, e)}
                            documentCount={customFolderDocumentCounts?.get(folder.id)}
                            updatedAt={customFolderUpdatedAt?.get(folder.id)}
                            createdAt={customFolderCreatedAt?.get(folder.id)}
                            showCheckbox={!!onCustomFolderCheckboxChange}
                            onCheckboxChange={onCustomFolderCheckboxChange ? (checked) => onCustomFolderCheckboxChange(folder, checked) : undefined}
                        />
                    );
                }
                const container = item.container;
                return (
                    <FolderCard
                        key={`container-${container.id}`}
                        type="container"
                        container={container}
                        viewMode={viewMode}
                        useMixedLayout={isMixedList}
                        isSelected={selectedItems.includes(`container-${container.id}`)}
                        onClick={() => onContainerClick?.(container)}
                        onContextMenu={(e) => onContainerContextMenu?.(container, e)}
                        documentCount={containerDocumentCounts?.get(container.id)}
                        updatedAt={containerUpdatedAt?.get(container.id)}
                        createdAt={containerCreatedAt?.get(container.id)}
                        showCheckbox={!!onContainerCheckboxChange}
                        onCheckboxChange={onContainerCheckboxChange ? (checked) => onContainerCheckboxChange(container, checked) : undefined}
                    />
                );
            })}

            {/* Documents */}
            {sortedDocuments.map((document) => (
                <DocumentCard
                    key={document.id}
                    document={document}
                    viewMode={viewMode}
                    isSelected={selectedItems.includes(document.id)}
                    onClick={() => onDocumentClick?.(document)}
                    onContextMenu={(e) => onDocumentContextMenu?.(document, e)}
                    onCheckboxChange={onDocumentCheckboxChange ? (checked) => onDocumentCheckboxChange(document, checked) : undefined}
                    showCheckbox={!!onDocumentCheckboxChange}
                    onVersionSelect={onVersionSelect}
                />
            ))}
            </div>
        </div>
    );
};

